import { Colors, FOCUS_RING, Icon, Text, TextInput } from '@amelio/ui-library';

import { COMPENSATION_CAPTION, COMPENSATION_TABLE_ROW } from '../compensationStyles';
import { MAX_INDIVIDUAL_INCREASE_PERCENT } from '../constants';
import { parseTypedNumber, useTypedNumber } from '../hooks/useTypedNumber';

import type {
    CompensationDraftLine,
    CompensationLevel,
    CompensationLine,
} from '@/types/compensation';

import { CustomPill } from '@/components/CustomPill/CustomPill';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getLocalizedText } from '@/utils/localization';
import { formatCurrency, formatDecimal, formatPercent } from '@/utils/numberFormat';

/** Shown wherever a figure has no value yet: no score, no known salary, no level. */
const EMPTY_VALUE = '—';

/** The separator belongs to the row, not to each of its seven cells: drawn per cell, the column
 * gap cut it into seven dashes. */
const CELL_CLASS_NAME = 'min-w-0 py-[12px]';

interface AdjustmentRowProps {
    /** The server's computation of this person's line: every figure on the row comes from here. */
    line: CompensationLine;
    /** The same person's inputs in the local draft: what the three editable fields show. */
    draftLine: CompensationDraftLine;
    level: CompensationLevel | undefined;
    canEdit: boolean;
    canRecordSalaries: boolean;
    onSalaryChange: (salary: number | null) => void;
    onPercentChange: (percent: number | null) => void;
    onToggleLock: () => void;
    onNoteChange: (note: string) => void;
}

/**
 * One person of the envelope: who they are, how they performed, what they earn, what the
 * distribution proposes, what they will actually get, and the note that explains why.
 */
export const AdjustmentRow = ({
    line,
    draftLine,
    level,
    canEdit,
    canRecordSalaries,
    onSalaryChange,
    onPercentChange,
    onToggleLock,
    onNoteChange,
}: AdjustmentRowProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const salaryText = draftLine.salary === null ? '' : String(draftLine.salary);
    const [salaryValue, setSalaryTyped] = useTypedNumber(salaryText);

    const percentText =
        draftLine.chosenPercent === null
            ? line.appliedPercent.toFixed(2)
            : String(draftLine.chosenPercent);
    const [percentValue, setPercentTyped] = useTypedNumber(percentText);

    const handleSalaryChange = (raw: string) => {
        const salary = parseTypedNumber(raw);

        setSalaryTyped({ raw, nextValueText: salary === null ? '' : String(salary) });
        onSalaryChange(salary);
    };

    const handlePercentChange = (raw: string) => {
        const percent = parseTypedNumber(raw);
        const clamped =
            percent === null ? null : Math.min(percent, MAX_INDIVIDUAL_INCREASE_PERCENT);

        setPercentTyped({
            raw,
            nextValueText: clamped === null ? line.appliedPercent.toFixed(2) : String(clamped),
        });
        onPercentChange(clamped);
    };

    return (
        <div
            role={'row'}
            className={COMPENSATION_TABLE_ROW}
            data-testid={`compensation-adjustment-row-${line.employeeId}`}
        >
            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                <Text
                    weight={'semibold'}
                    color={Colors.NAVY}
                    truncate
                >
                    {line.displayName}
                </Text>

                <Text
                    color={Colors.BASE_500}
                    className={COMPENSATION_CAPTION}
                    truncate
                >
                    {[line.jobTitle ?? EMPTY_VALUE, line.managerName].filter(Boolean).join(' · ')}
                </Text>
            </div>

            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                {level ? (
                    <CustomPill color={level.color}>{getLocalizedText(level.label)}</CustomPill>
                ) : (
                    <Text color={Colors.BASE_500}>{EMPTY_VALUE}</Text>
                )}

                <Text
                    color={Colors.BASE_500}
                    className={`${COMPENSATION_CAPTION} mt-1 block`}
                >
                    {line.score === null
                        ? EMPTY_VALUE
                        : t('compensation.scoreOutOfFive', {
                              score: formatDecimal(line.score, language),
                          })}

                    {line.scoreSource === 'Calibrated'
                        ? ` · ${t('compensation.adjustments.calibratedBadge')}`
                        : ''}
                </Text>
            </div>

            <div
                role={'cell'}
                className={`${CELL_CLASS_NAME} flex justify-end`}
            >
                {/* Right-aligned, like the money it holds, so the thousands line up. */}
                <div className={'w-[110px]'}>
                    <TextInput
                        type={'number'}
                        textAlign={'right'}
                        size={'sm'}
                        value={salaryValue}
                        onChange={handleSalaryChange}
                        readOnly={!canEdit || !canRecordSalaries}
                        ariaLabel={`${t('compensation.adjustments.columns.salary')} — ${line.displayName}`}
                        dataTestId={`compensation-salary-input-${line.employeeId}`}
                    />
                </div>
            </div>

            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                {/* The alignment goes on `Text` itself, not on the cell: the library's `Text`
                    carries its own `text-left`, which wins over a `text-center` inherited from
                    an ancestor. Same reason for the two money cells below. */}
                <Text
                    color={Colors.BASE_500}
                    align={'center'}
                    className={'tabular-nums'}
                >
                    {formatPercent(line.proposedPercent, language, { fractionDigits: 2 })}
                </Text>
            </div>

            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                <div className={'flex items-center justify-center gap-1.5'}>
                    <div className={'w-[84px]'}>
                        {/* A hand-entered percentage takes the row off the automatic
                            redistribution, so the field says so on its own edge rather than
                            leaving the lock beside it to carry the whole message. The value is
                            centred over the amount it produces, under a centred header. */}
                        <TextInput
                            type={'number'}
                            textAlign={'center'}
                            size={'sm'}
                            className={
                                draftLine.chosenPercent === null
                                    ? undefined
                                    : 'border-purple text-purple'
                            }
                            value={percentValue}
                            onChange={handlePercentChange}
                            readOnly={!canEdit}
                            ariaLabel={`${t('compensation.adjustments.columns.increase')} — ${line.displayName}`}
                            dataTestId={`compensation-percent-input-${line.employeeId}`}
                        />
                    </div>

                    {/* The unit the field itself cannot draw. Decorative: each field already
                        names itself through `ariaLabel`, and the column header repeats it. */}
                    <span
                        aria-hidden
                        className={`${COMPENSATION_CAPTION} text-base-500`}
                    >
                        %
                    </span>

                    {canEdit ? (
                        <button
                            type={'button'}
                            onClick={onToggleLock}
                            aria-label={t(
                                draftLine.isLocked
                                    ? 'compensation.adjustments.lock.unlock'
                                    : 'compensation.adjustments.lock.lock',
                            )}
                            data-testid={`compensation-lock-button-${line.employeeId}`}
                            className={`${FOCUS_RING} hover:bg-base-50 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] transition-colors ${
                                draftLine.isLocked ? 'bg-purple/10' : ''
                            }`}
                        >
                            <Icon
                                name={draftLine.isLocked ? 'security-lock' : 'security-unlock'}
                                size={'sm'}
                                color={draftLine.isLocked ? Colors.PURPLE : Colors.BASE_400}
                                ariaHidden
                            />
                        </button>
                    ) : null}
                </div>

                <div className={'mt-1 flex items-center justify-center gap-1'}>
                    <Text
                        color={Colors.BASE_500}
                        className={`${COMPENSATION_CAPTION} tabular-nums`}
                    >
                        {formatCurrency(line.amount, language)}
                    </Text>

                    {line.exceedsIndividualCap ? (
                        <>
                            <Icon
                                name={'essential-warning-2'}
                                size={'xs'}
                                color={Colors.WARNING}
                                ariaHidden
                            />

                            {/* The sentence, not just the glyph: a warning nobody can read is not
                                a warning. Visually the icon carries it; here it is spelled out. */}
                            <span className={'sr-only'}>
                                {t('compensation.adjustments.exceedsCap', {
                                    max: formatPercent(MAX_INDIVIDUAL_INCREASE_PERCENT, language),
                                })}
                            </span>
                        </>
                    ) : null}
                </div>
            </div>

            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                <Text
                    weight={'semibold'}
                    color={Colors.NAVY}
                    align={'right'}
                    className={'tabular-nums'}
                >
                    {line.newSalary === null
                        ? EMPTY_VALUE
                        : formatCurrency(line.newSalary, language)}
                </Text>
            </div>

            <div
                role={'cell'}
                className={CELL_CLASS_NAME}
            >
                <TextInput
                    size={'sm'}
                    value={draftLine.note ?? ''}
                    // Wrapped rather than passed straight through: the library's `TextInput` calls
                    // its `onChange` with `(value, event)`, and handing it the prop directly would
                    // forward the change event to a callback that only ever means the note.
                    onChange={(note) => onNoteChange(note)}
                    readOnly={!canEdit}
                    placeholder={t('compensation.adjustments.notePlaceholder')}
                    ariaLabel={`${t('compensation.adjustments.columns.note')} — ${line.displayName}`}
                    dataTestId={`compensation-note-input-${line.employeeId}`}
                />
            </div>
        </div>
    );
};
